import { CompensationType, EmploymentType } from 'gql-types';

export const renderCompensationType = (compensationType: CompensationType): string => {
    switch (compensationType) {
        case CompensationType.HOURLY:
            return 'Hourly';
        case CompensationType.HOURLY_PLUS_PER_VISIT_BONUS:
            return 'Hourly + Per-Visit Bonus';
        case CompensationType.PER_VISIT:
            return 'Per Visit';
        case CompensationType.SALARIED:
            return 'Salaried';
        default:
            // Fall back to the raw value rather than throwing: this renders in the
            // clinician header, so a compensation type the frontend hasn't mapped yet
            // (e.g. a newly-added backend enum) shouldn't take down the whole header.
            return compensationType;
    }
};

export const renderEmploymentType = (employmentType: EmploymentType): string => {
    switch (employmentType) {
        case EmploymentType.CONTRACTOR:
            return 'Contractor';
        case EmploymentType.FULL_TIME:
            return 'Full-Time';
        case EmploymentType.PART_TIME:
            return 'Part-Time';
        case EmploymentType.PRN:
            return 'PRN';
        case EmploymentType.UNKNOWN:
            return 'Unknown';
        default:
            // Fall back to the raw value rather than throwing (matches
            // renderCompensationType): a newly-added backend enum shouldn't take
            // down the clinician header it renders in.
            return employmentType;
    }
};

export const availableEmploymentTypes = Object.values(EmploymentType).map((code) => ({
    code,
    label: renderEmploymentType(code),
}));

/* UNKNOWN is the value a clinician carries when nobody has set an employment type, so it
 * describes the record rather than the person and is not worth showing.
 */
export const isKnownEmploymentType = (employmentType: EmploymentType): boolean =>
    employmentType !== EmploymentType.UNKNOWN;

// A clinician's employment type for display, empty when no type has been set.
export const employmentTypeLabel = (employmentType: EmploymentType): string =>
    isKnownEmploymentType(employmentType) ? renderEmploymentType(employmentType) : '';

// The parenthetical a clinician's name is followed by, empty when no type has been set.
export const employmentTypeSuffix = (employmentType: EmploymentType): string => {
    const label = employmentTypeLabel(employmentType);
    return label === '' ? '' : ` (${label})`;
};
